/**
 * AnalyticsProvider — owns the session_id singleton and the in-memory
 * batch queue, exposes both through context to descendant hooks.
 *
 * Mount below CerberusAuthProvider/AuthGate so the queue can capture the
 * authed `user_id`/`tenant_id` when emitting envelopes. While the relay URL
 * is absent (preview/local builds) the queue stays inert and `useTrack`
 * routes envelopes to `console.debug`.
 */
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { getRuntimeConfig } from '@/lib/runtime-config'
import { ulid } from '@/lib/ulid'
import { useActivityPing } from '@/modules/growth/useActivityPing'
import { AnalyticsQueue } from './queue'

const SESSION_KEY = 'ariadne_session_id'

interface AnalyticsContextValue {
  sessionId: string
  queue: AnalyticsQueue | null
}

const AnalyticsContext = createContext<AnalyticsContextValue | null>(null)

function readOrCreateSessionId(): string {
  if (typeof sessionStorage === 'undefined') return ulid()
  try {
    const existing = sessionStorage.getItem(SESSION_KEY)
    if (existing) return existing
    const next = ulid()
    sessionStorage.setItem(SESSION_KEY, next)
    return next
  } catch {
    return ulid()
  }
}

export function AnalyticsProvider({ children }: { children: ReactNode }) {
  const sessionIdRef = useRef<string>(readOrCreateSessionId())
  const [queue, setQueue] = useState<AnalyticsQueue | null>(null)

  // Once-per-day Peitho activity ping (feeds the "3 distinct days" referral
  // milestone). AnalyticsProvider mounts only inside the authed tree, so this
  // runs exactly when the authenticated user opens the app. Best-effort.
  useActivityPing()

  useEffect(() => {
    let active = true
    void getRuntimeConfig()
      .then((cfg) => {
        if (!active) return
        const endpoint = cfg.api.analytics_relay
        if (!endpoint) return
        setQueue(new AnalyticsQueue({ endpoint }))
      })
      .catch(() => {
        // runtime-config errors surfaced by AuthProvider; do not redo here.
      })
    return () => {
      active = false
    }
  }, [])

  useEffect(() => {
    if (!queue) return
    const flushUnload = () => queue.flush('unload')
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') queue.flush('unload')
    }
    window.addEventListener('pagehide', flushUnload)
    window.addEventListener('beforeunload', flushUnload)
    document.addEventListener('visibilitychange', onVisibility)
    return () => {
      window.removeEventListener('pagehide', flushUnload)
      window.removeEventListener('beforeunload', flushUnload)
      document.removeEventListener('visibilitychange', onVisibility)
      queue.flush('unload')
    }
  }, [queue])

  const value = useMemo<AnalyticsContextValue>(
    () => ({ sessionId: sessionIdRef.current, queue }),
    [queue],
  )

  return <AnalyticsContext.Provider value={value}>{children}</AnalyticsContext.Provider>
}

export function useAnalyticsContext(): AnalyticsContextValue | null {
  return useContext(AnalyticsContext)
}
